import React, { useEffect, useMemo, useState } from 'react';
import PresenceDot from '../components/PresenceDot';
import Modal from '../components/Modal';
import Avatar from '../components/Avatar';
import { useConfirm } from '../components/ConfirmDialog';
import {
  createGroup, updateGroup, addMembers, removeMember, groupRules
} from '../lib/chatLink';

/**
 * The dialogs that go with group conversations on the chat screen: starting a
 * group, and seeing - and changing - who is in one.
 *
 * Kept out of ChatPage.js because that file is already one long component
 * holding three panes, and these are forms with their own state that the page
 * only needs to hear the result of. Styled from ChatPage.css, .ch- like
 * everything else on that screen.
 *
 * WHO CAN BE ADDED is not decided here. The picker offers the people in your
 * own conversation list, because that list IS the set of people the server
 * lets you message - and a group must not be a way to reach somebody the
 * directory rules in Settings keep you from. The server refuses anyone else
 * regardless; offering only the right people means nobody is shown a choice
 * that will be refused.
 */

const why = (err, fallback) => err?.response?.data?.error || fallback;

/**
 * Choose several people from your directory.
 *
 * A search box over a list of tick boxes rather than a type-ahead, because an
 * administrator's list is the whole firm and a contractor's is three people,
 * and both are served by seeing who is there. The search narrows what is
 * SHOWN, never what is chosen: ticking Marco, searching for Rita and ticking
 * her keeps Marco.
 */
export const PeoplePicker = ({ people, exclude, chosen, onChange, idPrefix = 'pick' }) => {
  const [query, setQuery] = useState('');
  const offered = useMemo(
    () => (people || []).filter((p) => !(exclude && exclude.has(p.key))),
    [people, exclude]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return offered;
    return offered.filter((p) => `${p.name || ''} ${p.email || ''}`.toLowerCase().includes(q));
  }, [offered, query]);

  const toggle = (key) => onChange(chosen.includes(key)
    ? chosen.filter((k) => k !== key)
    : [...chosen, key]);

  if (offered.length === 0) {
    return (
      <p className="ch-empty">
        Everybody you can message is already here. You can add only people who
        are in your own conversation list.
      </p>
    );
  }

  return (
    <div className="ch-picker">
      <label className="ch-say-label" htmlFor={`${idPrefix}-search`}>Search people</label>
      <input
        id={`${idPrefix}-search`}
        className="ch-field ch-picker-search"
        type="search"
        value={query}
        placeholder="Search people…"
        onChange={(e) => setQuery(e.target.value)}
      />
      <ul className="ch-picker-list">
        {shown.map((p) => (
          <li key={p.key}>
            <label className="ch-pick">
              <input
                type="checkbox"
                value={p.key}
                checked={chosen.includes(p.key)}
                onChange={() => toggle(p.key)}
              />
              <Avatar person={p} className="ch-avatar" aria-hidden="true" />
              <span className="ch-pick-name">{p.name || p.email}</span>
              {p.is_contractor && <span className="ch-tag">IT</span>}
            </label>
          </li>
        ))}
        {shown.length === 0 && <li className="ch-empty">Nobody by that name.</li>}
      </ul>
      <p className="ch-picker-count" aria-live="polite">
        {chosen.length === 0 ? 'Nobody chosen yet'
          : chosen.length === 1 ? '1 person chosen' : `${chosen.length} people chosen`}
      </p>
    </div>
  );
};

/*
 * MEMBERS BY RULE (migration 079). Gordon, 23 October: choose members by
 * rule as well as one by one - "All staff", "All contractors", "Everyone", a
 * department - and have the group follow the firm as people join and leave.
 * Which rules are offered is the server's answer (GET /chat/group-rules):
 * only those this person may use, never wider than who they may message.
 * Each says how many it covers today. A server before 079 offers none, and
 * the dialog is the one it always was.
 */
const useRules = () => {
  const [rules, setRules] = useState([]);
  useEffect(() => {
    let live = true;
    groupRules()
      .then((got) => { if (live) setRules(Array.isArray(got?.rules) ? got.rules : []); })
      .catch(() => { if (live) setRules([]); });
    return () => { live = false; };
  }, []);
  return rules;
};

/** The rule tick boxes, with how many each covers. */
export const RulePicker = ({ rules, chosen, onChange, idPrefix = 'rule' }) => (
  <fieldset className="ch-rules">
    <legend className="ch-field-label">Everyone who is…</legend>
    {rules.map((r) => (
      <label className="ch-pick ch-rule" key={r.key}>
        <input
          type="checkbox"
          id={`${idPrefix}-${r.key}`}
          value={r.key}
          checked={chosen.includes(r.key)}
          onChange={() => onChange(chosen.includes(r.key)
            ? chosen.filter((k) => k !== r.key) : [...chosen, r.key])}
        />
        <span className="ch-pick-name">{r.label}</span>
        {r.count !== null && r.count !== undefined && (
          <span className="ch-rule-count">{r.count === 1 ? '1 person' : `${r.count} people`}</span>
        )}
      </label>
    ))}
    <p className="ch-foot">
      The group keeps itself up to date: people join it and leave it as they
      join and leave the firm.
    </p>
  </fieldset>
);

/** Start a group: a name, what it is for, and who is in it - by rule, by name, or both. */
export const NewGroupDialog = ({ people, onClose, onCreated }) => {
  const [name, setName] = useState('');
  const [topic, setTopic] = useState('');
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const offeredRules = useRules();
  const [rules, setRules] = useState([]);
  const [left, setLeft] = useState([]);

  const create = async (event) => {
    if (event) event.preventDefault();
    const clean = name.trim();
    if (!clean || busy) return;
    setBusy(true);
    setError(null);
    try {
      const made = await createGroup({
        name: clean, topic: topic.trim() || undefined, members: chosen,
        rules, exclude: left.filter((k) => !chosen.includes(k))
      });
      onCreated(made);
    } catch (err) {
      setError(why(err, 'Could not start that group'));
      setBusy(false);
    }
  };

  return (
    <Modal
      title="New group"
      subtitle="A conversation with several people that is not about any one ticket."
      onClose={onClose}
      footer={(
        <div className="ch-dialog-actions">
          <button type="button" className="ch-flat" onClick={onClose}>Cancel</button>
          <button
            type="submit"
            form="ch-new-group"
            className="ch-primary"
            disabled={busy || !name.trim()}
          >
            {busy ? 'Starting…' : 'Start group'}
          </button>
        </div>
      )}
    >
      <form id="ch-new-group" className="ch-form" onSubmit={create}>
        <label className="ch-field-label" htmlFor="ch-group-name">Name</label>
        <input
          id="ch-group-name"
          className="ch-field"
          type="text"
          maxLength={80}
          value={name}
          placeholder="e.g. Office move"
          onChange={(e) => setName(e.target.value)}
        />
        <label className="ch-field-label" htmlFor="ch-group-topic">
          What it is for <span className="ch-optional">(optional)</span>
        </label>
        <input
          id="ch-group-topic"
          className="ch-field"
          type="text"
          maxLength={200}
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />
        <p className="ch-field-label">Who is in it</p>
        {offeredRules.length > 0 && (
          <RulePicker rules={offeredRules} chosen={rules} onChange={setRules} idPrefix="ch-new-rule" />
        )}
        {offeredRules.length > 0 && (
          <p className="ch-field-label">{rules.length ? 'And these people' : 'Or choose people'}</p>
        )}
        <PeoplePicker people={people} chosen={chosen} onChange={setChosen} idPrefix="ch-new" />
        {/* Left out of the rule on purpose - and kept out as it updates. */}
        {rules.length > 0 && (
          <details className="ch-leave-out">
            <summary>Leave somebody out{left.length ? ` (${left.length})` : ''}</summary>
            <PeoplePicker
              people={(people || []).filter((p) => !p.is_me)}
              chosen={left}
              onChange={setLeft}
              idPrefix="ch-new-leave"
            />
          </details>
        )}
        <p className="ch-foot">
          Anything about a particular job still belongs on that ticket, where the
          approver and whoever picks it up next will read it.
        </p>
        {error && <p className="ch-error" role="alert">{error}</p>}
      </form>
    </Modal>
  );
};

/** The rules a group follows, changed by whoever manages it. None stops following. */
const RuleEditor = ({ current, labels, busy, onCancel, onSave }) => {
  const offered = useRules();
  const [chosen, setChosen] = useState(current);
  // A rule the group follows that this person may not use is still shown, so
  // it is not dropped by accident; the server checks the rest.
  const kept = current.filter((k) => !offered.some((r) => r.key === k))
    .map((k, i) => ({ key: k, label: labels[current.indexOf(k)] || labels[i] || k, count: null }));
  const all = [...offered, ...kept];
  return (
    <>
      {all.length > 0
        ? <RulePicker rules={all} chosen={chosen} onChange={setChosen} idPrefix="ch-edit-rule" />
        : <p className="ch-empty">No rules you may use. An administrator decides who may message whom, in Settings → Chat, calls &amp; meetings → Messaging.</p>}
      {current.length > 0 && chosen.length === 0 && (
        <p className="ch-foot">With no rule, everybody in the group now stays, as if added by name.</p>
      )}
      <div className="ch-dialog-actions">
        <button type="button" className="ch-flat" onClick={onCancel}>Cancel</button>
        <button type="button" className="ch-primary" disabled={busy} onClick={() => onSave(chosen)}>
          Save rules
        </button>
      </div>
    </>
  );
};

/**
 * Who is in a group, and the things you may do about it.
 *
 * Anybody in the group may add people - the same as Slack, and harmless
 * because they can add only people they could already message. Removing
 * somebody else, and renaming, is for whoever the server says may manage it
 * (`may_manage`: the owner, or an administrator). Leaving is always yours.
 */
export const MembersDialog = ({ info, people, myKey, onClose, onChanged, onLeft }) => {
  const [adding, setAdding] = useState(false);
  const [chosen, setChosen] = useState([]);
  const [name, setName] = useState(info.name || '');
  const [topic, setTopic] = useState(info.topic || '');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [confirm, confirmDialog] = useConfirm();

  const members = useMemo(() => info.members || [], [info.members]);
  // 079: what the group follows, and - for whoever manages it - changing that.
  const following = Array.isArray(info.rule_labels) ? info.rule_labels : [];
  const [ruling, setRuling] = useState(false);
  const inside = useMemo(() => new Set(members.map((m) => m.key)), [members]);
  const manage = info.may_manage === true;

  const run = async (what, fn) => {
    setBusy(what);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(why(err, 'That did not work'));
    } finally {
      setBusy(null);
    }
  };

  const rename = (event) => {
    event.preventDefault();
    const clean = name.trim();
    if (!clean) return;
    run('rename', async () => {
      const changed = await updateGroup(info.id, { name: clean, topic: topic.trim() });
      onChanged(changed);
    });
  };

  const add = () => run('add', async () => {
    const res = await addMembers(info.id, chosen);
    setChosen([]);
    setAdding(false);
    onChanged(res?.members ? { ...info, members: res.members } : null);
  });

  const remove = async (m) => {
    if (!(await confirm({ title: `Remove ${m.name} from ${info.name}?`, confirmLabel: 'Remove', danger: true }))) return;
    run(`remove:${m.key}`, async () => {
      await removeMember(info.id, m.key);
      onChanged({ ...info, members: members.filter((x) => x.key !== m.key) });
    });
  };

  const leave = async () => {
    // Asked, because it is not undone from here: getting back in needs
    // somebody still inside to add you again.
    if (!(await confirm({ title: `Leave ${info.name}?`, message: 'Somebody in it will have to add you back.',
      confirmLabel: 'Leave', danger: true }))) return;
    run('leave', async () => {
      await removeMember(info.id, myKey);
      onLeft();
    });
  };

  /*
   * ARCHIVE (21 October: "allow chats to be archived"). Folds the group away
   * for everybody in it - it goes under "Archived" in the list, with its
   * history, and any member can bring it back from there. Whoever manages
   * the group does it, as with renaming; asked first, because the whole
   * group loses the conversation until somebody unfolds it.
   */
  const archive = async () => {
    if (!(await confirm({ title: `Archive ${info.name}?`,
      message: 'It moves under Archived for everybody in it; any member can bring it back.', confirmLabel: 'Archive' }))) return;
    run('archive', async () => {
      await updateGroup(info.id, { archived: true });
      onLeft();
    });
  };

  return (
    <Modal
      title={`#${info.name}`}
      subtitle={info.topic || undefined}
      onClose={onClose}
    >
      {manage && (
        <form className="ch-form ch-rename" onSubmit={rename}>
          <label className="ch-field-label" htmlFor="ch-rename-name">Name</label>
          <input
            id="ch-rename-name"
            className="ch-field"
            type="text"
            maxLength={80}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <label className="ch-field-label" htmlFor="ch-rename-topic">What it is for</label>
          <input
            id="ch-rename-topic"
            className="ch-field"
            type="text"
            maxLength={200}
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
          />
          <div className="ch-dialog-actions">
            <button
              type="submit"
              className="ch-flat"
              disabled={busy === 'rename' || !name.trim()
                || (name.trim() === info.name && topic.trim() === (info.topic || ''))}
            >
              Save name and topic
            </button>
          </div>
        </form>
      )}

      {(following.length > 0 || (manage && info.topics_ready === true)) && (
        <div className="ch-form ch-following">
          <p className="ch-field-label">Members by rule</p>
          <p className="ch-foot">
            {following.length
              ? `Follows ${following.join(', ')}: people join and leave as they join and leave the firm.`
              : 'Everybody here was added by name.'}
          </p>
          {manage && !ruling && (
            <div className="ch-dialog-actions">
              <button type="button" className="ch-flat" onClick={() => setRuling(true)}>
                {following.length ? 'Change rules' : 'Add a rule'}
              </button>
            </div>
          )}
          {manage && ruling && (
            <RuleEditor
              current={info.member_rules || []}
              labels={following}
              busy={busy === 'rules'}
              onCancel={() => setRuling(false)}
              onSave={(next) => run('rules', async () => {
                const changed = await updateGroup(info.id, { rules: next });
                setRuling(false);
                onChanged(changed);
              })}
            />
          )}
        </div>
      )}

      <div className="ch-form">
        <p className="ch-field-label">
          {members.length === 1 ? '1 member' : `${members.length} members`}
        </p>
        <ul className="ch-members">
          {members.map((m) => (
            <li key={m.key} className="ch-member">
              <Avatar person={m} className="ch-avatar" aria-hidden="true" />
              <span className="ch-member-name">
                {m.name}{m.key === myKey && <span className="ch-you"> (you)</span>}
              </span>
              {/* A dot, not the word "About" the people list uses: here it sits
                  beside a name in a list of names, and the title says what it
                  means for anybody who wonders. */}
              {/* The same dot the people list draws, from the same answer -
                  this used to be its own `online` test with its own copy of
                  "ninety seconds", which stopped being survivable once a
                  status could say Do not disturb. */}
              <PresenceDot state={m.status || (m.online ? 'available' : 'offline')}
                label={m.status_label} detail={m.status_detail} className="pd-inline" />
              {m.role === 'owner' && <span className="ch-tag">Owner</span>}
              {m.via_rule && <span className="ch-tag ch-tag-rule" title="In the group by its rule">Rule</span>}
              {m.is_contractor && <span className="ch-tag">IT</span>}
              {manage && m.key !== myKey && (
                <button
                  type="button"
                  className="ch-flat ch-small"
                  disabled={busy === `remove:${m.key}`}
                  onClick={() => remove(m)}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      <div className="ch-form">
        {!adding && (
          <div className="ch-dialog-actions ch-dialog-split">
            <button type="button" className="ch-flat" onClick={() => setAdding(true)}>
              Add people
            </button>
            {manage && (
              <button
                type="button"
                className="ch-flat ch-archive-group"
                disabled={busy === 'archive'}
                onClick={archive}
              >
                Archive group
              </button>
            )}
            {myKey && (
              <button
                type="button"
                className="ch-flat ch-danger"
                disabled={busy === 'leave'}
                onClick={leave}
              >
                Leave group
              </button>
            )}
          </div>
        )}
        {adding && (
          <>
            <p className="ch-field-label">Add people</p>
            <PeoplePicker
              people={people}
              exclude={inside}
              chosen={chosen}
              onChange={setChosen}
              idPrefix="ch-add"
            />
            <div className="ch-dialog-actions">
              <button type="button" className="ch-flat"
                onClick={() => { setAdding(false); setChosen([]); }}>
                Cancel
              </button>
              <button
                type="button"
                className="ch-primary"
                disabled={busy === 'add' || chosen.length === 0}
                onClick={add}
              >
                {chosen.length > 1 ? `Add ${chosen.length} people` : 'Add'}
              </button>
            </div>
          </>
        )}
      </div>

      {error && <p className="ch-error" role="alert">{error}</p>}
      {confirmDialog}
    </Modal>
  );
};
